import React, { useState, useEffect } from 'react';
import { useEffectOnce } from 'react-use';
import { useHistory, useRouteMatch, useLocation } from 'react-router-dom';
import { useMutation } from '@apollo/client';

import Cart from 'components/consumer/cart';
import useScreen from 'hooks/useScreen';
import { GET_CART } from 'mocks/queries';
import { MERGE_SHARED_CART } from 'mocks/mutations';
import CtaLink from 'components/essentials/Cta/CtaLink';
import PDPAlertBar from 'components/consumer/PDP/PDPAlertBar';
import ProgressLoader from 'components/essentials/ProgressLoader/ProgressLoader';
import SimpleHeader from 'components/consumer/consumer-header/simple-header';
import CartFooter from 'components/consumer/consumer-footer/CartFooter';
import isClient from 'commons/isClient';
import refreshFinancingContent from 'commons/refreshFinancingContent';
import getZipcode from 'commons/getZipcode';
import BrowserOnly from 'components/consumer/BrowserOnly';
import useAffirmScript from 'hooks/useAffirmScript';
import useSuspenseQuery from 'commons/useSuspenseQuery';

const CartPageContainer = () => {
  const history = useHistory();
  const match = useRouteMatch();
  const location = useLocation();
  const { isMobile } = useScreen();
  const [state, setState] = useState({ loading: true, error: null });
  const [utmQueryString] = useState(() => location?.search || '');
  const [mergeSharedCart] = useMutation(MERGE_SHARED_CART);
  const { refetch } = useSuspenseQuery(GET_CART, {
    skip: true,
    fetchPolicy: 'network-only',
  });

  useAffirmScript({
    onLoad: refreshFinancingContent,
  });

  useEffect(() => {
    if (isClient()) {
      setTimeout(() => {
        refreshFinancingContent();
      }, 5500);
    }
  }, []);

  useEffectOnce(() => {
    const zipCode = getZipcode();
    const {
      params: { saved_cart_token },
    } = match;
    if (saved_cart_token) {
      mergeSharedCart({
        variables: {
          zipCode,
          saved_cart_token,
        },
      })
        .then(res => {
          const { success, errors } = res?.data?.mergeShareCart || {};
          if (success) {
            refetch({
              zipCode,
            }).then(() => {
              history.push(`/cart${utmQueryString}`);
            });
          } else {
            setState({
              loading: false,
              error:
                errors?.[0]?.message || 'Error merging cart. Please try again.',
            });
          }
        })
        .catch(e => {
          setState({
            loading: false,
            error: e.message,
          });
        });
    } else {
      refetch({
        zipCode,
      }).then(() => {
        setState({
          loading: false,
        });
      });
    }
  });

  return (
    <div className="bg-white m-0 flex flex-col w-full">
      <div className="items-center flex flex-row justify-start min-h-10 md:justify-center md:p-8">
        <CtaLink
          to="/"
          hasBackArrow
          noLinkPadding
          hasArrow={false}
          className="block text-xs text-gray normal-case md:absolute md:top-8 md:left-8 no-underline"
          color={isMobile ? 'black' : 'brand'}
        >
          Continue shopping
        </CtaLink>

        {!isMobile && <SimpleHeader headerClassNames="border-b-0" />}
      </div>

      <div className="md:min-h-[calc(100vh-250px)]">
        {state.loading ? (
          <BrowserOnly>
            <div className="text-center pt-10">
              <ProgressLoader size={12} />
            </div>
          </BrowserOnly>
        ) : (
          <div className="bg-white mt-0 mx-auto mb-8 md:border md:border-solid md:border-gray-light7 md:max-w-[450px] md:p-9">
            {state.error && (
              <PDPAlertBar className="mt-0 mb-8" type="error">
                {state.error}
              </PDPAlertBar>
            )}
            <Cart viewType="cart-only" />
          </div>
        )}
      </div>

      {!isMobile && <CartFooter />}
    </div>
  );
};

export default CartPageContainer;
